import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import FormInput from '../components/FormInput';
import PasswordInput from '../components/PasswordInput';
import FormButton from '../components/FormButton';
import ValidationMessage from '../components/ValidationMessage';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import FuturaSmileIcon from '../components/icons/FuturaSmileIcon';
import ChevronLeftIcon from '../components/icons/ChevronLeftIcon';
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/validation';
import { getReferralCode, clearReferralCode } from '../utils/referralUtils';
import toast from 'react-hot-toast';

const RegisterPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Password Strength State
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState('');

  // Validation states
  const [usernameValidation, setUsernameValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [emailValidation, setEmailValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [passwordValidation, setPasswordValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');

  // Error messages
  const [usernameError, setUsernameError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const { register, loginWithGoogle, error: authError, isLoading } = useAuth();
  const navigate = useNavigate();

  // Google Login
  const loginToGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Retrieve referral code if present
        const referralCode = getReferralCode();

        await loginWithGoogle(tokenResponse.access_token, referralCode || undefined);

        // Clear referral code after successful Google authentication
        clearReferralCode();

        toast.success('Account created successfully!');
        navigate('/dashboard');
      } catch (err: any) {
        console.error('Google signup error:', err);
        toast.error(err.message || 'Google signup failed');
      }
    },
    onError: () => {
      toast.error('Google signup failed');
    }
  });

  // Live Password Validation
  useEffect(() => {
    if (password) {
      const val = validatePassword(password);
      setPasswordStrength(val.strength);
      setPasswordStrengthText(val.strengthText);
    } else {
      setPasswordStrength(0);
      setPasswordStrengthText('');
    }
  }, [password]);

  // ... (maintain validations)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Full Name Validation (Basic Length Check)
    let usernameErrorMsg = '';
    const trimmedUsername = username.trim();
    if (trimmedUsername.length < 3) {
      usernameErrorMsg = 'Full Name must be at least 3 characters long';
    } else if (trimmedUsername.length > 50) {
      usernameErrorMsg = 'Full Name must be less than 50 characters';
    }

    if (usernameErrorMsg) {
      setUsernameError(usernameErrorMsg);
      setUsernameValidation('invalid');
      return;
    } else {
      setUsernameError('');
      setUsernameValidation('valid');
    }

    // 2. Email Validation
    const emailVal = validateEmail(email);
    if (!emailVal.valid) {
      setEmailError(emailVal.error || '');
      setEmailValidation('invalid');
      return;
    } else {
      setEmailError('');
      setEmailValidation('valid');
    }

    // 3. Password Validation
    const passwordVal = validatePassword(password);
    if (!passwordVal.valid) {
      setPasswordError(passwordVal.errors.join(', ') || 'Invalid password');
      setPasswordValidation('invalid');
      return;
    } else {
      setPasswordError('');
      setPasswordValidation('valid');
    }

    // 4. Confirm Password Validation
    const confirmVal = validateConfirmPassword(password, confirmPassword);
    if (!confirmVal.valid) {
      setConfirmPasswordError(confirmVal.error || '');
      setConfirmPasswordValidation('invalid');
      return;
    } else {
      setConfirmPasswordError('');
      setConfirmPasswordValidation('valid');
    }

    setIsSubmitting(true);
    try {
      // Auto-generate username from Full Name 
      // Strategy: Lowercase -> Remove all non-alphanumeric chars -> Ensure min length
      let sanitizedUsername = trimmedUsername.toLowerCase().replace(/[^a-z0-9_]/g, '');

      // Fallback if sanitization leaves it too short (e.g. "A. B." -> "ab")
      if (sanitizedUsername.length < 3) {
        // Pad with random numbers to satisfy backend requirement
        sanitizedUsername += Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      }

      // Retrieve referral code if present
      const referralCode = getReferralCode();

      await register(sanitizedUsername, email, password, referralCode || undefined);

      // Clear referral code after successful registration
      clearReferralCode();

      setRegistrationSuccess(true);
      toast.success('Account created successfully!');
    } catch (err: any) {
      console.error('Registration error:', err);
      toast.error(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };



  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-zinc-900 rounded-xl p-8 shadow-2xl border border-zinc-800 text-center"
        >
          <div className="w-16 h-16 bg-teal-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Verify your email</h2>
          <p className="text-gray-400 mb-8">
            We've sent a verification link to <span className="text-gray-200 font-medium">{email}</span>.
            Please check your inbox (and spam folder) to activate your account.
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
            >
              Proceed to Login
            </button>
            <button
              onClick={() => setRegistrationSuccess(false)} // Just in case they mistyped email
              className="text-sm text-gray-500 hover:text-gray-400"
            >
              Use a different email
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex">
      {/* Decorative Branding Panel - Hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/sign-in/background.png)'
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-8 h-full">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div className="flex items-center space-x-3">
              <img
                src="/images/Logo.webp"
                alt="FuturaSmile Logo"
                className="h-8 w-auto object-contain"
              />
            </div>

            <motion.button
              onClick={() => navigate('/')}
              whileHover={{ x: -2 }}
              className="flex items-center space-x-2 px-4 py-2 rounded-full border border-gray-300 text-gray-200 hover:bg-white/10 transition-all duration-300"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span className="text-sm font-medium">Back to website</span>
            </motion.button>
          </div>

          {/* Quote */}
          <div className="max-w-md">
            <blockquote className="text-2xl font-serif text-white leading-relaxed">
              "Every future smile starts with one decisive moment. Make it yours."
            </blockquote>
          </div>
        </div>
      </div>

      {/* Authentication Form Panel */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="mb-8">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h1 className="text-3xl font-bold text-gray-200 mb-2">
                Create an account
              </h1>
              <p className="text-gray-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-teal-400 hover:text-teal-300 transition-colors"
                >
                  Log in
                </Link>
              </p>
            </motion.div>
          </div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
            onSubmit={handleSubmit}
          >
            <FormInput
              id="username"
              name="username"
              type="text"
              label="Full Name"
              value={username}
              onChange={setUsername}
              placeholder="Enter your full name"
              required
              autoComplete="name"
            />

            <FormInput
              id="email"
              name="email"
              type="email"
              label="Email address"
              value={email}
              onChange={setEmail}
              placeholder="Enter your email"
              required
              autoComplete="email"
              validationState={emailValidation}
              error={emailError}
            />

            <PasswordInput
              id="password"
              name="password"
              label="Password"
              value={password}
              onChange={setPassword}
              onFocus={() => setShowPasswordStrength(true)}
              placeholder="Create a strong password"
              required
              autoComplete="new-password"
              validationState={passwordValidation}
              error={passwordError}
              showStrengthMeter={showPasswordStrength}
              strength={passwordStrength}
              strengthText={passwordStrengthText}
            />

            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
              validationState={confirmPasswordValidation}
              error={confirmPasswordError}
            />

            {/* Error Message */}
            {authError && (
              <ValidationMessage
                type="error"
                message={authError}
              />
            )}

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <FormButton
                type="submit"
                loading={isLoading || isSubmitting}
                disabled={isLoading || isSubmitting}
                fullWidth
                size="lg"
                variant="teal"
              >
                {isLoading || isSubmitting ? 'Creating account...' : 'Create account'}
              </FormButton>
            </motion.div>
          </motion.form>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-8"
          >
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-zinc-900 text-gray-400">Or continue with</span>
              </div>
            </div>
          </motion.div>

          {/* Social Login Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 flex justify-center"
          >
            <button
              type="button"
              onClick={() => loginToGoogle()}
              className="flex items-center justify-center w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-200 hover:bg-zinc-800 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;